/**
 * CreateRoutePanel - Panel para crear rutas de recolección
 *
 * @version 1.0
 * @module core/modules/waste/components/panels
 *
 * @description
 * Panel que se abre al terminar de dibujar una ruta en el mapa.
 * Permite ingresar propiedades básicas y crear la ruta en el backend.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Route as RouteIcon, Loader2, Zap } from 'lucide-react';
import type { LineString } from 'geojson';
import { useProject } from '../../../../projects';
import { useRoutes } from '../../hooks/useRoutes';
import { useContainers } from '../../hooks/useContainers';
import { COLLECTION_FREQUENCIES, DAYS_OF_WEEK, DEFAULT_ROUTE_START_TIME } from '../../constants';
import type { CollectionFrequency, DayOfWeek, WasteContainer } from '../../types';
import {
  calculateLineStringDistance,
  formatDistance,
  estimateRouteDuration,
  formatDuration,
  optimizeContainerOrder,
  generateGeometryFromContainers,
  calculateOptimizationImprovement,
} from '../../utils/route-geometry-utils';
import './create-route-panel.css';

/**
 * Props del CreateRoutePanel
 */
interface CreateRoutePanelProps {
  /**
   * ID de la ruta (si existe, modo edición; si no, modo creación)
   */
  routeId?: string;

  /**
   * Geometría de la ruta dibujada
   */
  geometry: LineString;

  /**
   * IDs de contenedores pre-asignados (opcional)
   */
  containerIds?: string[];

  /**
   * Indica si todavía estamos en modo dibujo (agregando puntos)
   * @default false
   */
  isDrawing?: boolean;

  /**
   * Callback al cerrar el panel
   */
  onClose: () => void;

  /**
   * Callback al crear/actualizar exitosamente
   */
  onCreated?: () => void;
}

/**
 * Genera un código sugerido para la ruta
 */
function generateRouteCode(existingCount: number): string {
  const number = (existingCount + 1).toString().padStart(3, '0');
  return `RT-${number}`;
}

export function CreateRoutePanel({
  routeId,
  geometry,
  containerIds = [],
  isDrawing = false,
  onClose,
  onCreated,
}: CreateRoutePanelProps): JSX.Element {
  const { projectId } = useProject();
  const { routes, createRoute, editRoute } = useRoutes({ projectId: projectId || '' });
  const { getContainerById } = useContainers({
    projectId: projectId || '',
    autoLoad: true,
  });

  // Determinar si estamos en modo edición o creación
  const isEditMode = !!routeId;
  const existingRoute = isEditMode ? routes.find(r => r.id === routeId) : null;

  // Form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState<CollectionFrequency>('daily');
  const [operatingDays, setOperatingDays] = useState<DayOfWeek[]>([
    'MON',
    'TUE',
    'WED',
    'THU',
    'FRI',
  ]);
  const [startTime, setStartTime] = useState(DEFAULT_ROUTE_START_TIME);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optimizationMessage, setOptimizationMessage] = useState<string | null>(null);

  // Geometry and containers state (puede cambiar con optimización)
  const [currentGeometry, setCurrentGeometry] = useState<LineString>(geometry);
  const [currentContainerIds, setCurrentContainerIds] = useState<string[]>(containerIds);

  // Métricas calculadas
  const distance = calculateLineStringDistance(currentGeometry);
  const duration = estimateRouteDuration(distance, currentContainerIds.length);

  // Cargar datos de ruta existente al montar (modo edición)
  useEffect(() => {
    if (existingRoute) {
      setCode(existingRoute.code);
      setName(existingRoute.name);
      setDescription(existingRoute.description || '');
      setFrequency(existingRoute.frequency);
      setOperatingDays(existingRoute.operatingDays);
      setStartTime(existingRoute.startTime);
      setCurrentGeometry(existingRoute.geometry);
      setCurrentContainerIds(existingRoute.containerIds);
      console.log('[CreateRoutePanel] Datos de ruta cargados:', existingRoute.code);
    } else {
      // Modo creación: auto-generar código
      setCode(generateRouteCode(routes.length));
    }
  }, [existingRoute, routes.length]);

  // Sincronizar geometría cuando cambia externamente (durante el dibujo)
  useEffect(() => {
    setCurrentGeometry(geometry);
  }, [geometry]);

  /**
   * Validar formulario
   */
  const validate = useCallback((): string | null => {
    if (!code.trim()) {
      return 'El código es requerido';
    }

    if (!name.trim()) {
      return 'El nombre es requerido';
    }

    if (operatingDays.length === 0) {
      return 'Debe seleccionar al menos un día de operación';
    }

    if (!geometry || !geometry.coordinates || geometry.coordinates.length < 2) {
      return 'La geometría de la ruta es inválida';
    }

    return null;
  }, [code, name, operatingDays, geometry]);

  /**
   * Manejar submit del formulario
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      if (!projectId) {
        setError('No hay proyecto activo');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        if (isEditMode && routeId) {
          // Modo edición: actualizar ruta existente
          await editRoute(routeId, {
            code: code.trim(),
            name: name.trim(),
            geometry: currentGeometry,
            containerIds: currentContainerIds,
            frequency,
            estimatedDuration: duration,
            estimatedDistance: distance,
            operatingDays,
            startTime,
          });

          console.log('[CreateRoutePanel] Ruta actualizada:', routeId);

          // Dispatch evento de actualización
          window.dispatchEvent(new CustomEvent('route-updated'));
        } else {
          // Modo creación: crear nueva ruta
          await createRoute({
            code: code.trim(),
            name: name.trim(),
            geometry: currentGeometry,
            containerIds: currentContainerIds,
            frequency,
            estimatedDuration: duration,
            estimatedDistance: distance,
            operatingDays,
            startTime,
            projectId,
            status: 'active',
          });

          console.log('[CreateRoutePanel] Ruta creada');

          // Dispatch evento de creación
          window.dispatchEvent(new CustomEvent('route-created'));
        }

        // Callback opcional
        if (onCreated) {
          onCreated();
        }

        // Cerrar panel
        onClose();
      } catch (err) {
        console.error('[CreateRoutePanel] Error:', err);
        setError(`Error al ${isEditMode ? 'actualizar' : 'crear'} la ruta. Por favor intenta nuevamente.`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validate,
      projectId,
      code,
      name,
      geometry,
      containerIds,
      frequency,
      duration,
      distance,
      operatingDays,
      startTime,
      createRoute,
      onCreated,
      onClose,
    ]
  );

  /**
   * Toggle día de operación
   */
  const toggleOperatingDay = (day: DayOfWeek): void => {
    setOperatingDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
  };

  /**
   * Optimizar orden de contenedores
   */
  const handleOptimize = useCallback(async () => {
    if (currentContainerIds.length < 3) return;

    setIsOptimizing(true);
    setOptimizationMessage(null);

    try {
      // Cargar datos completos de contenedores
      const containers: WasteContainer[] = [];
      for (const id of currentContainerIds) {
        const container = getContainerById(id);
        if (container) {
          containers.push(container);
        }
      }

      if (containers.length < currentContainerIds.length) {
        console.warn('[CreateRoutePanel] Some containers not found');
        setError('No se pudieron cargar todos los contenedores');
        return;
      }

      // Guardar orden original para comparación
      const originalContainers = [...containers];

      // Optimizar orden
      const optimizedContainers = optimizeContainerOrder(containers);

      // Calcular mejora
      const improvement = calculateOptimizationImprovement(
        originalContainers,
        optimizedContainers
      );

      // Si hay mejora significativa, actualizar
      if (improvement.improvement > 0.01) {
        // Al menos 10 metros de mejora
        const newGeometry = generateGeometryFromContainers(optimizedContainers);

        if (newGeometry) {
          setCurrentGeometry(newGeometry);
          setCurrentContainerIds(optimizedContainers.map((c) => c.id));

          setOptimizationMessage(
            `Ruta optimizada: ${formatDistance(improvement.improvement)} menos (${improvement.improvementPercent.toFixed(1)}% mejora)`
          );

          // Auto-ocultar mensaje después de 5 segundos
          setTimeout(() => setOptimizationMessage(null), 5000);
        }
      } else {
        setOptimizationMessage('La ruta ya está optimizada');
        setTimeout(() => setOptimizationMessage(null), 3000);
      }
    } catch (err) {
      console.error('[CreateRoutePanel] Error optimizing:', err);
      setError('Error al optimizar la ruta');
    } finally {
      setIsOptimizing(false);
    }
  }, [currentContainerIds, getContainerById]);

  return (
    <div className="crp-panel">
      {/* Header */}
      <header className="crp-header">
        <div className="crp-header-title">
          <RouteIcon size={20} />
          <div>
            <h2>{isEditMode ? 'Editar Ruta' : 'Nueva Ruta de Recolección'}</h2>
            {isDrawing && (
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                fontWeight: 'normal',
              }}>
                ✏️ Agregando puntos... Presione Enter para finalizar
              </p>
            )}
          </div>
        </div>
        <button
          className="crp-close-btn"
          onClick={onClose}
          aria-label={isDrawing ? "Cancelar dibujo" : "Cerrar panel"}
          disabled={isSubmitting}
          title={isDrawing ? "Cancelar dibujo y cerrar panel" : "Cerrar panel"}
        >
          <X size={20} />
        </button>
      </header>

      <form className="crp-form" onSubmit={handleSubmit}>
        {/* Métricas */}
        <section className="crp-metrics">
          <div className="crp-metric-card">
            <span className="crp-metric-label">Distancia</span>
            <span className="crp-metric-value">{formatDistance(distance)}</span>
          </div>
          <div className="crp-metric-card">
            <span className="crp-metric-label">Duración est.</span>
            <span className="crp-metric-value">{formatDuration(duration)}</span>
          </div>
          <div className="crp-metric-card">
            <span className="crp-metric-label">Contenedores</span>
            <span className="crp-metric-value">{currentContainerIds.length}</span>
          </div>
        </section>

        {/* Optimización */}
        {currentContainerIds.length >= 3 && (
          <div className="crp-optimization">
            <button
              type="button"
              className="crp-optimize-btn"
              onClick={() => void handleOptimize()}
              disabled={isOptimizing || isSubmitting}
              title="Optimizar orden de contenedores para minimizar distancia"
            >
              {isOptimizing ? (
                <>
                  <Loader2 size={14} className="crp-spinner" />
                  <span>Optimizando...</span>
                </>
              ) : (
                <>
                  <Zap size={14} />
                  <span>Optimizar orden de contenedores</span>
                </>
              )}
            </button>

            {optimizationMessage && (
              <div className="crp-optimization-message">{optimizationMessage}</div>
            )}
          </div>
        )}

        {/* Campos básicos */}
        <section className="crp-section">
          <div className="crp-field">
            <label htmlFor="route-code" className="crp-label">
              Código *
            </label>
            <input
              id="route-code"
              type="text"
              className="crp-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="RT-001"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="crp-field">
            <label htmlFor="route-name" className="crp-label">
              Nombre *
            </label>
            <input
              id="route-name"
              type="text"
              className="crp-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ruta Centro"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="crp-field">
            <label htmlFor="route-description" className="crp-label">
              Descripción
            </label>
            <textarea
              id="route-description"
              className="crp-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción opcional..."
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </section>

        {/* Frecuencia */}
        <section className="crp-section">
          <div className="crp-field">
            <label htmlFor="route-frequency" className="crp-label">
              Frecuencia *
            </label>
            <select
              id="route-frequency"
              className="crp-select"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as CollectionFrequency)}
              required
              disabled={isSubmitting}
            >
              {COLLECTION_FREQUENCIES.map((freq) => (
                <option key={freq.value} value={freq.value}>
                  {freq.label}
                </option>
              ))}
            </select>
          </div>

          <div className="crp-field">
            <label className="crp-label">Días de operación *</label>
            <div className="crp-days-grid">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  className={`crp-day-btn ${operatingDays.includes(day.value) ? 'active' : ''}`}
                  onClick={() => toggleOperatingDay(day.value)}
                  disabled={isSubmitting}
                  title={day.label}
                >
                  {day.short}
                </button>
              ))}
            </div>
          </div>

          <div className="crp-field">
            <label htmlFor="route-start-time" className="crp-label">
              Hora de inicio *
            </label>
            <input
              id="route-start-time"
              type="time"
              className="crp-input"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </div>
        </section>

        {/* Error */}
        {error && (
          <div className="crp-error" role="alert">
            {error}
          </div>
        )}

        {/* Footer */}
        <footer className="crp-footer">
          <button
            type="button"
            className="crp-btn crp-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="crp-btn crp-btn-primary"
            disabled={isSubmitting || isDrawing}
            title={isDrawing ? 'Presione Enter para finalizar el dibujo primero' : ''}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="crp-spinner" />
                {isEditMode ? 'Guardando...' : 'Creando...'}
              </>
            ) : isDrawing ? (
              '✏️ Dibujando...'
            ) : (
              isEditMode ? 'Guardar Cambios' : 'Crear Ruta'
            )}
          </button>
        </footer>
      </form>
    </div>
  );
}
